import './open-image-modal.less';
import classNames from 'classnames';
import { useState } from 'react';
import { Loading } from '@meitu/saas-ui';
import { loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { PluginEnum } from '@meitu/saas-editor-core/es/version-types';
import ImageUpload from '@meitu/saas-editor-ui/es/components/image-upload';
import IconFont from '@meitu/xdesign-icon-font';
import { AI_MATTING_IMAGE } from '@/editor/extensions/ai-matting/components/ai-matting-extension';
import { events, PLUGIN_OPEN_EVENT } from '@/events-bus';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { usePluginModel } from '@/store/models/plugin-model';
import { checkIsImageEditorPath } from '@/utils/common';
import { IMAGE_TRANSLATION } from '../../constants';
import useOpenPlugins from '../../hooks/use-open-plugins';
import { AI_EXPEND_IMAGE } from '../ai-expend-image/ai-expend-image';
import { AI_MODIFY_IMAGE } from '../ai-modify-image/ai-modify-image';

const TYPE_TITLE = () => {
    const { $t } = getLocaleStore();

    return {
        [AI_EXPEND_IMAGE]: $t((v) => v.collapse.moreCollapse.imageTool.aiExpend.title),
        [AI_MODIFY_IMAGE]: $t((v) => v.collapse.moreCollapse.imageTool.aiModify.title),
        [AI_MATTING_IMAGE]: $t((v) => v.extensions.aiMatting.title),
        [IMAGE_TRANSLATION]: $t((v) => v.collapse.moreCollapse.imageTool.imageTranslation.title),
    };
};

const OpenImageModal = () => {
    const { $t } = useLocaleStore();
    const setOpenImageModalType = usePluginModel((actions) => actions.setOpenImageModalType);
    const openImageModalType = usePluginModel((state) => state.openImageModalType);

    const [isLoading, setIsLoading] = useState(false);
    const isImageEditor = checkIsImageEditorPath();
    const { openPlugin } = useOpenPlugins();
    const closeHandle = () => {
        setOpenImageModalType('');
        editor.services.mask.hide();
    };

    const uploadImageProps: any = {
        children: (
            <>
                <div className="btn">
                    <IconFont fontSize={16} color="var(--content-btn-primary)" name="UploadBold" />
                    {$t((v) => v.collapse.moreCollapse.imageModal.uploadImage)}
                </div>
            </>
        ),
        beforeUpload() {
            setIsLoading(true);
        },
        async onSuccess(url: string, imgData: any) {
            const mainLayerWidth = editor.services.frame.getActiveFrameSize().width;
            const mainLayerHeight = editor.services.frame.getActiveFrameSize().height;

            const img = await loadImageAsync(url);
            const { width, height } = img;

            const point = {
                top: 0,
                left: 0,
            };
            let imgWidth: number = 0;
            let imgHeight: number = 0;
            if (mainLayerWidth / mainLayerHeight > width / height) {
                imgHeight = mainLayerHeight / 2;
                const scale = height / imgHeight;
                imgWidth = width / scale;
            } else {
                imgWidth = mainLayerWidth / 2;
                const scale = width / imgWidth;
                imgHeight = height / scale;
            }

            const centerPoint = {
                x: mainLayerWidth / 2,
                y: mainLayerHeight / 2,
            };

            point.left = centerPoint.x - imgWidth / 2;
            point.top = centerPoint.y - imgHeight / 2;

            editor.imageLayer.addImageLayer({
                url,
                width: img.width,
                height: img.height,
                left: point.left,
                top: point.top,
                isPhoto: false,
                isAILayer: false,
            });
            setOpenImageModalType('');
            setIsLoading(false);

            if (openImageModalType === AI_EXPEND_IMAGE) {
                openPlugin({ func: PluginEnum.AI_EXPEND_IMAGE });
            } else if (openImageModalType === AI_MODIFY_IMAGE) {
                openPlugin({ func: PluginEnum.AI_MODIFY_IMAGE });
            } else if (openImageModalType) {
                events.emit(PLUGIN_OPEN_EVENT, {
                    func: openImageModalType,
                    isExtension: true,
                });
            }
        },
        onError(error: any) {
            setIsLoading(false);
        },
    };
    return (
        <div className="c-openImageModel">
            <div
                className={classNames({
                    wrap: 'true',
                    loading: isLoading,
                })}
            >
                <div className="content">
                    <div className="img">
                        <IconFont fontSize={54} color="var(--content-system-info-icon-regular)" name="InfoCircleFill" />
                    </div>
                    <div className="title">
                        {$t((v, { r }) =>
                            r(v.collapse.moreCollapse.imageModal.title, [
                                openImageModalType
                                    ? TYPE_TITLE()[openImageModalType as keyof ReturnType<typeof TYPE_TITLE>]
                                    : '',
                            ]),
                        )}
                    </div>

                    <ImageUpload {...uploadImageProps} />

                    {!isImageEditor && (
                        <div className="tip" onClick={closeHandle}>
                            {$t((v) => v.collapse.moreCollapse.imageModal.desc)}
                        </div>
                    )}
                </div>

                <div className="close" onClick={closeHandle}>
                    <IconFont fontSize={14} color="var(--content-system-tertiary)" name="CrossBlack" />
                </div>
            </div>

            {isLoading ? (
                <div className="loading">
                    <Loading />
                </div>
            ) : null}
        </div>
    );
};

export default OpenImageModal;
